<template>
  <div class="editor-section">
    <div class="form-group">
      <label class="form-label required-label">訊息內容</label>
      <textarea
        ref="textRef"
        class="form-control message-textarea"
        :value="modelValue.text"
        @input="onTextInput"
        @click="syncCaret"
        @keyup="syncCaret"
        @select="syncCaret"
        @blur="syncCaret"
        rows="6"
        placeholder="請輸入文字；表情以 {{$1}}、{{$2}} 佔位，金額建議用 NT$、元"
        maxlength="5000"
      ></textarea>
      <div class="text-actions">
        <LineEmojiPicker @pick="onEmojiPick" />
        <div v-if="emojisParseError" class="emoji-json-error" role="alert">{{ emojisParseError }}</div>
      </div>
      <div class="insert-hint" role="note">
        <i class="ri-information-line" aria-hidden="true"></i>
        <ul class="insert-hint-list">
          <li>
            請先在<strong>訊息內容</strong>點選插入位置，再按「<strong>LINE表情符號</strong>」。會插入
            <code v-pre>{{$1}}</code>、<code v-pre>{{$2}}</code> 等佔位，系統會自動記錄對應的表情；可隨意剪貼移動；刪除某個佔位時，對應的表情設定會一併移除。
          </li>
          <li>
            <strong>佔位後面還接了其他文字而變不完整</strong>（例如 <code v-pre>{{$1}</code> 後面不是第二個 <code>}</code>），該段會整段清除，請重新用「LINE表情符號」插入。
          </li>
          <li>
            在<strong>訊息結尾</strong>少打一個 <code>}</code>，可先補回以還原 <code v-pre>{{$1}}</code>；若<strong>誤刪開頭一個 <code>{</code></strong>變成 <code v-pre>{$1}}</code> 等，也可在結尾<strong>補回一個 <code>{</code></strong>還原。
          </li>
          <li>
            <strong>儲存時</strong>會換算為 LINE API 的半形 <code>$</code> 與 <code>index</code>。一般金額請用「NT$」「元」。未先定位游標則插在文字最末端。
          </li>
        </ul>
      </div>
      <div class="char-count" :class="{ danger: lengthRisk === 'over', warn: lengthRisk === 'near' }">
        {{ lengthInfo.apiLength }} / {{ LINE_TEXT_MAX_LENGTH }} 字元
        <span v-if="lengthInfo.variables.length">
          ・替換變數後最多約 {{ lengthInfo.worstCaseLength }} 字元
        </span>
      </div>
      <div v-if="lengthRisk" class="length-risk" :class="lengthRisk" role="alert">
        <i class="ri-error-warning-line" aria-hidden="true"></i>
        <span v-if="lengthRisk === 'over'">
          插入的取代符號替換成實際資料後，最多可能達 {{ lengthInfo.worstCaseLength }} 字元，超過 LINE 的
          {{ LINE_TEXT_MAX_LENGTH }} 字元上限。發送時系統會自動截斷以免整則發送失敗，
          建議先縮短內容或減少取代符號。
        </span>
        <span v-else>
          已接近 {{ LINE_TEXT_MAX_LENGTH }} 字元上限（替換變數後最多約 {{ lengthInfo.worstCaseLength }} 字元）。
          會員名稱、LINE 暱稱等長度不一，建議多預留空間。
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, ref } from 'vue';
import {
  LINE_EMOJI_BRACKET_RE,
  stripIncompleteLineEmojiBrackets,
  mapCaretAfterRemovingRanges
} from '@/utils/lineEmojiPlaceholder.js';
import {
  estimateLineTextLength,
  LINE_TEXT_MAX_LENGTH,
  LINE_TEXT_WARN_LENGTH
} from '@/utils/lineTextLengthEstimate.js';
import LineEmojiPicker from './LineEmojiPicker.vue';

const props = defineProps({
  modelValue: { type: Object, required: true },
  /** 群發範本不替換取代符號，不需估算替換後長度 */
  substituteVariables: { type: Boolean, default: true }
});
const emit = defineEmits(['update:modelValue']);

/** 字數與「替換取代符號後的最壞情況長度」 */
const lengthInfo = computed(() => {
  const info = estimateLineTextLength(props.modelValue.text);
  if (props.substituteVariables) return info;
  return { ...info, worstCaseLength: info.apiLength, variables: [] };
});
/** '' | 'near'（接近滿載）| 'over'（替換後可能超過上限） */
const lengthRisk = computed(() => {
  const worst = lengthInfo.value.worstCaseLength;
  if (worst > LINE_TEXT_MAX_LENGTH) return 'over';
  if (worst > LINE_TEXT_WARN_LENGTH) return 'near';
  return '';
});
const textRef = ref(null);
/** 記住游標（開選擇器時 textarea 會失焦，需靠 blur 前寫入） */
const savedCaretPos = ref(null);

/** 訊息內目前出現的 slot 編號（含結尾可補完的佔位，避免誤刪 JSON） */
function emojiSlotsInText(text) {
  const set = new Set();
  const t = text || '';
  const re = new RegExp(LINE_EMOJI_BRACKET_RE.source, 'g');
  let m;
  while ((m = re.exec(t)) !== null) {
    set.add(parseInt(m[1], 10));
  }
  const oneBrace = t.match(/\{\{\$(\d+)\}\s*$/u);
  if (oneBrace) set.add(parseInt(oneBrace[1], 10));
  const digitsSuffix = t.match(/\{\{\$(\d+)\s*$/u);
  if (digitsSuffix) {
    const tail = t.slice(t.lastIndexOf('{{$'));
    if (!tail.includes('}')) {
      set.add(parseInt(digitsSuffix[1], 10));
    }
  }
  // 誤刪開頭一個 {：{$n}} / {$n} / {$n（結尾可補 `{`）
  const sgTwo = t.match(/\{\$(\d+)\}\}\s*$/u);
  if (sgTwo) set.add(parseInt(sgTwo[1], 10));
  const sgOne = t.match(/\{\$(\d+)\}\s*$/u);
  if (sgOne && !/\{\$(\d+)\}\}\s*$/u.test(t)) {
    set.add(parseInt(sgOne[1], 10));
  }
  const sgDigits = t.match(/\{\$(\d+)\s*$/u);
  if (sgDigits) {
    const tail = t.slice(t.lastIndexOf('{$'));
    if (!tail.includes('}')) {
      set.add(parseInt(sgDigits[1], 10));
    }
  }
  return set;
}

/**
 * 訊息刪掉 {{$n}} 時，同步刪除 JSON 內對應 slot（JSON 語法錯誤時不動 emojisJson，避免覆寫使用者正在編的內容）
 * @returns {string|null} 新的 emojisJson；null 表示不必改 emojisJson
 */
function pruneEmojisJsonToMatchText(newText, emojisJsonRaw) {
  const parsed = parseEmojisList(emojisJsonRaw || '');
  if (parsed === null) return null;
  const slots = emojiSlotsInText(newText);
  const filtered = parsed.filter((e) => slots.has(Number(e?.slot)));
  if (filtered.length === parsed.length) return null;
  return filtered.length ? JSON.stringify(filtered, null, 2) : '';
}

function onTextInput(e) {
  const ta = e.target;
  const rawText = ta.value;
  const caretBefore =
    typeof ta.selectionStart === 'number' ? ta.selectionStart : rawText.length;

  const { text: baseText, removed } = stripIncompleteLineEmojiBrackets(rawText);
  const stripped = baseText !== rawText;

  if (stripped) {
    const newCaret = mapCaretAfterRemovingRanges(caretBefore, removed);
    nextTick(() => {
      const el = textRef.value;
      if (el) {
        el.selectionStart = el.selectionEnd = newCaret;
        savedCaretPos.value = newCaret;
      }
    });
  } else {
    syncCaret(e);
  }

  const pruned = pruneEmojisJsonToMatchText(baseText, props.modelValue.emojisJson);
  const next = { ...props.modelValue, text: baseText };
  if (pruned !== null) next.emojisJson = pruned;
  emit('update:modelValue', next);
}

function syncCaret(e) {
  const t = e?.target;
  if (t && typeof t.selectionStart === 'number') {
    savedCaretPos.value = t.selectionStart;
  }
}

function parseEmojisList(raw) {
  if (!raw || !String(raw).trim()) return [];
  try {
    const a = JSON.parse(raw);
    return Array.isArray(a) ? a : [];
  } catch {
    return null;
  }
}

const emojisParseError = computed(() => {
  const raw = (props.modelValue.emojisJson || '').trim();
  if (!raw) return '';
  const parsed = parseEmojisList(raw);
  if (parsed === null) return 'JSON 格式不正確';
  const seenSlot = new Set();
  for (let i = 0; i < parsed.length; i++) {
    const e = parsed[i];
    const slot = Number(e?.slot);
    if (!Number.isFinite(slot) || slot < 1 || !Number.isInteger(slot)) {
      return `第 ${i + 1} 筆：slot 須為正整數（對應 {{$1}}、{{$2}}）`;
    }
    if (seenSlot.has(slot)) return `第 ${i + 1} 筆：slot ${slot} 重複`;
    seenSlot.add(slot);
    if (typeof e?.productId !== 'string' || !e.productId.trim()) {
      return `第 ${i + 1} 筆：productId 須為字串`;
    }
    if (e?.emojiId === undefined || e?.emojiId === null || String(e.emojiId).trim() === '') {
      return `第 ${i + 1} 筆：emojiId 必填`;
    }
  }
  return '';
});

function computeNextEmojiSlot(text, emojisJson) {
  let max = 0;
  const re = new RegExp(LINE_EMOJI_BRACKET_RE.source, 'g');
  let m;
  while ((m = re.exec(text || '')) !== null) {
    const n = parseInt(m[1], 10);
    if (n > max) max = n;
  }
  const list = parseEmojisList(emojisJson || '');
  if (Array.isArray(list)) {
    for (const e of list) {
      const s = Number(e?.slot);
      if (Number.isFinite(s) && s > max) max = s;
    }
  }
  return max + 1;
}

/**
 * 在訊息內容游標處插入純文字（供父層「變數宣告」使用）；會經過與輸入相同的 strip / prune 流程。
 */
function insertPlainTextAtCaret(insert) {
  const chunk = insert ?? '';
  if (!chunk) return;
  const ta = textRef.value;
  const text = props.modelValue.text || '';
  let pos = savedCaretPos.value;
  if (pos === null || pos === undefined) {
    pos = ta && typeof ta.selectionStart === 'number' ? ta.selectionStart : text.length;
  }
  pos = Math.min(Math.max(0, pos), text.length);
  const rawText = text.slice(0, pos) + chunk + text.slice(pos);
  const caretBeforeInRaw = pos + chunk.length;

  const { text: baseText, removed } = stripIncompleteLineEmojiBrackets(rawText);
  const stripped = baseText !== rawText;
  let caretEnd = caretBeforeInRaw;
  if (stripped) {
    caretEnd = mapCaretAfterRemovingRanges(caretBeforeInRaw, removed);
  }

  const pruned = pruneEmojisJsonToMatchText(baseText, props.modelValue.emojisJson);
  const next = { ...props.modelValue, text: baseText };
  if (pruned !== null) next.emojisJson = pruned;
  emit('update:modelValue', next);

  nextTick(() => {
    const el = textRef.value;
    if (el) {
      el.focus();
      el.selectionStart = el.selectionEnd = Math.min(caretEnd, baseText.length);
      savedCaretPos.value = el.selectionStart;
    }
  });
}

defineExpose({ insertPlainTextAtCaret });

function onEmojiPick({ productId, emojiId }) {
  if (emojisParseError.value) {
    window.alert('請先修正表情資料後，再使用「LINE表情符號」。');
    return;
  }
  const ta = textRef.value;
  const text = props.modelValue.text || '';
  let pos = savedCaretPos.value;
  if (pos === null || pos === undefined) {
    pos = ta && typeof ta.selectionStart === 'number' ? ta.selectionStart : text.length;
  }
  pos = Math.min(Math.max(0, pos), text.length);

  const nextSlot = computeNextEmojiSlot(text, props.modelValue.emojisJson);
  const insert = `{{$${nextSlot}}}`;
  const newText = text.slice(0, pos) + insert + text.slice(pos);

  let emojis = parseEmojisList(props.modelValue.emojisJson || '');
  if (emojis === null) emojis = [];

  emojis = emojis.filter((e) => e && Number.isFinite(Number(e.slot)) && Number(e.slot) >= 1);
  emojis = emojis.filter((e) => Number(e.slot) !== nextSlot);
  emojis.push({ slot: nextSlot, productId, emojiId: String(emojiId ?? '') });
  emojis.sort((a, b) => Number(a.slot) - Number(b.slot));

  emit('update:modelValue', {
    ...props.modelValue,
    text: newText,
    emojisJson: JSON.stringify(emojis, null, 2)
  });

  nextTick(() => {
    if (!ta) return;
    ta.focus();
    const caret = pos + insert.length;
    ta.selectionStart = ta.selectionEnd = caret;
    savedCaretPos.value = caret;
  });
}
</script>

<style scoped>
.message-textarea { resize: vertical; min-height: 140px; font-size: 14px; }
.insert-hint {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  margin: 8px 0 0;
  padding: 10px 12px;
  font-size: 12px;
  line-height: 1.5;
  color: #4b5563;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}
.insert-hint > i {
  flex-shrink: 0;
  margin-top: 4px;
  color: #3b82f6;
  font-size: 16px;
}
.insert-hint-list {
  flex: 1;
  min-width: 0;
  margin: 0;
  padding-left: 1.15em;
  list-style-type: disc;
}
.insert-hint-list li {
  margin-bottom: 8px;
  line-height: 1.55;
}
.insert-hint-list li:last-child {
  margin-bottom: 0;
}
.insert-hint code {
  font-size: 11px;
  padding: 1px 4px;
  background: #e5e7eb;
  border-radius: 3px;
}
.text-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  margin-top: 10px;
}
.char-count { font-size: 12px; color: #9ca3af; text-align: right; margin-top: 4px; }
.char-count.warn { color: #d97706; }
.char-count.danger { color: #ef4444; }

.length-risk {
  display: flex; align-items: flex-start; gap: 6px;
  margin-top: 6px; padding: 8px 10px;
  border-radius: 6px; font-size: 12px; line-height: 1.5;
}
.length-risk i { flex-shrink: 0; font-size: 14px; margin-top: 1px; }
.length-risk.near { background: #fffbeb; border: 1px solid #fde68a; color: #92400e; }
.length-risk.over { background: #fef2f2; border: 1px solid #fecaca; color: #991b1b; }
.emoji-json-error { font-size: 12px; color: #ef4444; line-height: 1.45; max-width: 100%; }
</style>
